import {isPaid, type Mode, type Plan} from "home/gating";
import type {UpsellOffer} from "./UpsellApi";

/**
 * The 50%-off-first-month offer, as the CLIENT is allowed to understand it
 * (kitecorp/transferify#117, api kitecorp/transferify-api-service#838).
 *
 * <b>Nothing here knows what the discount is.</b> The percentage is owner-controlled from the Stripe
 * dashboard and reaches us only through `GET /pay/upsell-offer`, so this module decides WHETHER to
 * speak and never WHAT the number is. A literal percentage anywhere in the client would be a second
 * implementation of the offer: stale the moment the dashboard is edited, and simply wrong in an
 * environment where no coupon is armed.
 *
 * React-free on purpose (the repo's logic-outside-react rule): every moment in the upsell program
 * renders differently, and only these rules are shared between them.
 */

/** An offer worth putting on screen. */
export interface FirstMonthOffer {
	/** The percentage off the first month, exactly as the api quoted it. Never computed here. */
	percentOff: number;
}

/**
 * The api's real available remainder, or null when there is no count to show.
 *
 * Every positive whole count is useful campaign context, including all ten initial spots. Invalid counts
 * stay silent: an available spot is a positive whole unit, and the client must never repair or invent one.
 */
export function visibleOfferSpotsLeft(spotsLeft: number | null | undefined): number | null {
	if (typeof spotsLeft !== "number" || !Number.isInteger(spotsLeft) || spotsLeft <= 0) {
		return null;
	}
	return spotsLeft;
}

/**
 * The api's answer, turned into an offer or into silence.
 *
 * <b>Silence is the default for everything unusable</b>, and the cases are not hypothetical. `null` is
 * the api's ordinary "no offer" (ineligible buyer, yearly price, no coupon armed in this environment).
 * `undefined` is the same answer arriving through a generated client type that models an absent field.
 * Zero, negative and above-100 values cannot be phrased as a discount at all — "0% off" and "120% off"
 * are both worse than saying nothing — and a non-finite value would render as "NaN%".
 *
 * @param quoted the percentage the api quoted, or null/undefined for no offer
 * @return the offer, or null to render nothing
 */
export function firstMonthOfferFrom(quoted: number | null | undefined): FirstMonthOffer | null {
	if (typeof quoted !== "number" || !Number.isFinite(quoted)) {
		return null;
	}
	if (quoted <= 0 || quoted > 100) {
		return null;
	}
	return {percentOff: quoted};
}

/**
 * The scarcity the plan dialog may show from its own fresh per-tier offer reads.
 *
 * Opening the dialog refetches every quote because those are the prices the reader is deciding from.
 * A callout count captured before that fetch cannot survive here: the last redemption may have been
 * consumed in between, in which case the fresh responses carry no offer and the dialog must show
 * neither discount figures nor the stale urgency line. The first valid fresh quote is enough because
 * all tiers read the same Stripe promotion code and the api sends campaign metadata all-null-together.
 */
export function visibleFreshOfferSpotsLeft(
	offers: ReadonlyArray<UpsellOffer | null | undefined>,
): number | null {
	const freshOffer = offers.find(offer => firstMonthOfferFrom(offer?.percentOff) !== null);
	return freshOffer == null ? null : visibleOfferSpotsLeft(freshOffer.spotsLeft);
}

/** Everything the post-send moment needs in order to decide whether it exists. */
export interface PostSendUpsellInput {
	/** A discount was already presented to this account on an EARLIER screen today; see {@link silentFor}. */
	seenToday?: boolean;
	/**
	 * How the transfer that just finished was sent.
	 *
	 * Hosted sends qualify on their own. Instant qualifies only once {@link servedDownload} is true —
	 * see {@link postSendUpsell} for why the moment arrives later there rather than not at all.
	 */
	mode: Mode;
	/** The sender's current plan. */
	plan: Plan;
	/** What `GET /pay/upsell-offer` quoted, or null while unasked, in flight, or refused. */
	quotedPercentOff: number | null;
	/**
	 * A recipient has received a whole file through this instant session (ui#1665).
	 *
	 * Meaningless for a hosted send, where the upload itself is the completed transfer, so those
	 * callers pass `false` and the mode clause below never reads it.
	 */
	servedDownload: boolean;
	/**
	 * This sender has already taken the offer on this screen (review of ui#1736).
	 *
	 * Only the non-navigating path can ever pass `true`: the hosted CTA leaves for Stripe and never
	 * comes back, so nothing there survives to be offered twice. The instant CTA pays IN PLACE, and the
	 * plan it bought does not switch on until the `invoice.paid` webhook lands moments later — so
	 * without this the same row keeps offering, and pressing it again buys a SECOND subscription.
	 */
	claimed: boolean;
}

/**
 * The offer to show once a transfer has gone out, or null for none.
 *
 * <b>THE TRIGGER IS THE MOMENT, NOT THE MONEY — owner decision, 2026-08-26 (ui#1556).</b> This rule
 * used to take a third input, `paidOverLimit`, and speak only after a send the sender had paid an
 * over-limit one-off for; the note here said a line after every transfer "would be a different product
 * change nobody approved". The owner has now approved exactly that change: the offer belongs after any
 * completed transfer, because "that is the 'feel good' moment where everything went through". The input
 * is gone rather than defaulted, so no caller can revive the narrow trigger by accident.
 *
 * <b>INSTANT (P2P) WAITS FOR A COMPLETED DOWNLOAD — owner decision, 2026-08-30 (ui#1665).</b> It used
 * to be excluded outright, and the reasons were about TIMING rather than about audience. Instant's
 * success card opens on "waiting for recipient to connect": the link is live, nothing has arrived, and
 * the sender's own tab IS the transfer — the card says "keep this tab open" and "expires when you close
 * this tab". So the exclusion held until the moment the owner's own reason for this offer ("the feel
 * good moment where everything went through") actually arrives in P2P, which is a recipient finishing a
 * download. `servedDownload` is that instant, latched in `core/p2p/peerActivity` so it survives the
 * channel closing, and it is the only thing that changed: an instant sender still gets NOTHING while
 * the link waits or while bytes are in flight.
 *
 * <b>The prerequisite that made this answerable was a non-navigating CTA</b>, which now exists: the
 * claim dialog mints an EMBEDDED subscription checkout on this path (api `POST
 * /pay/create-embedded-checkout-session`, `redirect_on_completion=never`) and never a full-page
 * redirect, because that redirect is what would destroy the live transfer and any download running
 * through it. That is a property of the surface, not of this rule — but this rule is only correct while
 * it holds, so a future caller that reintroduces a redirect on the instant path has broken it.
 *
 * <b>The old copy objection is spent.</b> This rule used to note that the offer's pitch was about size
 * caps and server retention, neither of which instant has. ui#1653 removed the benefits sub-line and
 * ui#1672 removed the tier name, so nothing the offer says today claims either.
 *
 * <b>What did NOT change is where the moment lives.</b> ui#1454's constraint stands — no subscription
 * pitch competes with the over-limit checkout's single CTA — and it is unaffected by this widening,
 * because the moment is still AFTER the send. This module cannot enforce that on its own: it is the
 * caller's mounting point that decides, and `scripts/over-limit-single-cta-sweep.mjs` is what keeps
 * this module's names off the checkout surface — including, since ui#1556, a rule that this line has
 * exactly one importer, because the mounting point is now load-bearing on its own.
 *
 * <b>NO DISMISSAL INPUT, and that is a decision rather than an omission (ui#1556).</b> This moment and
 * the post-signup callout are both gated on a free plan, so one account can be eligible for both in a
 * session — but never at once: `CreateFlow` renders the composer that carries the callout only in its
 * `create` phase and the success screen that carries this line only in its `success` phase. Sequential
 * is the intended arrangement, so neither suppresses the other, and this rule therefore does not read
 * `./offerDismissal`. That store's own header says why in full.
 *
 * <b>The subscriber check is deliberately duplicated here</b> even though the api's own floor already
 * excludes anyone who has ever had a paid plan. The acceptance criterion "a user who already has a
 * subscription is never shown it" is a CLIENT-VISIBLE promise, and a promise proven only by a server
 * rule stops being proven the moment that rule is asked a different question. It costs one comparison
 * and it can fail loudly in a test — and it carries more weight since the widening than before it: the
 * send's own payment used to short-circuit this rule before `isPaid` was ever reached, so this is now
 * the ONLY client-side gate standing between a subscriber and a discount they cannot buy.
 *
 * <b>Two audiences it excludes as a side effect, recorded so nobody "fixes" them by accident.</b> A
 * referral TRIAL is served as `subscriptionActive: true, subscriptionType: BASIC`, so `planOf` reads a
 * trial reader as paid and this rule says nothing to them — deliberately: ui#1449 owns that audience
 * with a moment of its own, timed to the trial running out. And a CHURNED subscriber reads as free
 * here, so the client would speak to them; they are refused by the api's `hasEverHadPaidPlan` floor
 * alone. That was belt-and-braces before ui#1556 (they also had to have paid for an over-limit send)
 * and is server-only now, which is fine — the ticket's client-visible promise is about senders who
 * currently subscribe — but it is worth knowing which layer is holding it.
 */
export function postSendUpsell(input: PostSendUpsellInput): FirstMonthOffer | null {
	if (silentFor(input)) {
		return null;
	}
	return firstMonthOfferFrom(input.quotedPercentOff);
}

/** Everything {@link shouldAskForFirstMonthOffer} and {@link postSendUpsell} both gate on. */
export interface FirstMonthOfferAudience {
	/** A discount was already presented to this account on an EARLIER screen today; see {@link silentFor}. */
	seenToday?: boolean;
	/** How the transfer that just finished was sent. */
	mode: Mode;
	/** The sender's current plan. */
	plan: Plan;
	/** A recipient has received a whole file through this instant session (ui#1665). */
	servedDownload: boolean;
	/** This sender has already taken the offer on this screen; see {@link PostSendUpsellInput.claimed}. */
	claimed: boolean;
}

/**
 * The audience gate, written ONCE.
 *
 * Both exported rules call this rather than restating the clause, which is what the module header has
 * always asked for and what the ui#1665 widening made worth enforcing structurally: the clause now has
 * three inputs instead of two, and two hand-copied versions of a three-input rule is a drift waiting to
 * be shipped in whichever copy has no test on it.
 *
 * `claimed` is written FIRST because it is the only clause about money already spent: a sender who has
 * just paid must stop being asked whatever the other two say, including during the webhook window where
 * `plan` still reads free. The clauses are a plain OR with no side effects, so the order is for the
 * reader rather than for the machine — it used to claim a precedence the line did not have.
 *
 * <b>ANOTHER OFFER ON THE SAME SCREEN IS NOT A CLAUSE HERE — owner decision, 2026-09-15 (api#1704).</b>
 * There used to be a `couponPresented` input, and it silenced this gate as soon as the success card's
 * ladder coupon had been presented. It was written when that coupon was issued on a completed HOSTED
 * send only, where it could never withhold anything: the coupon is not even asked for until the
 * campaign answer has settled (`offers/ladder/couponCountdown`'s `couponAdmissionKey` reserves the
 * empty slot while a campaign request is in flight, and `currentLadderAccountKey` refuses outright
 * while `campaignActive`), so by the time a coupon presents on a hosted send the campaign is already
 * known to be absent and the flag closed a gate over nothing. Extending the coupon
 * to P2P (ui#2469) gave it something to withhold for the first time,
 * on the one path where it is wrong: an instant coupon is issued at LINK CREATION, minutes before the
 * download this moment waits for, so it closed the gate before the moment arrived and the campaign
 * offer could never displace it. `peel/peelTagSlot`'s `successGiftCandidates` ranks the campaign ABOVE
 * the coupon and the two share ONE slot, so the coupon holding that slot is precisely the state the
 * campaign has to be asked about — the owner's decision is that the campaign replaces it on P2P as it
 * already does on hosted. The input is GONE rather than defaulted to false, so no caller can revive
 * the suppression by passing it, and `seenToday` keeps the cross-screen rule it always had: a discount
 * presented on an EARLIER screen today still silences this one, because that flag is captured when the
 * success card mounts and never re-read while it is open.
 */
function silentFor({mode, plan, servedDownload, claimed, seenToday}: FirstMonthOfferAudience): boolean {
	return claimed || seenToday === true || isPaid(plan) || (mode === "instant" && !servedDownload);
}

/**
 * Whether it is worth ASKING the api about this sender at all.
 *
 * The gate {@link postSendUpsell} applies, minus the api's answer — which is the whole point: the
 * answer costs a live Stripe read on a page path, so a sender who could not be offered anything must
 * not trigger one. It lives here rather than in the component so the two cannot disagree; a component
 * that computed its own version of this would be a second eligibility rule, and the one that gates the
 * SPEND would be the one nobody tested.
 *
 * Unlike {@link shouldAskForPostSignupOffer} this needs no `sessionReady` gate: its moment cannot
 * arrive before the session has, because a transfer has to complete first.
 *
 * <b>Named arguments rather than positional (ui#1665).</b> The gate took `(mode, plan)` while both were
 * strings, and gained a third input that is neither — a caller mixing up two same-typed strings would
 * have compiled, and this is the gate that decides whether a live Stripe read is spent.
 *
 * @param audience the send, the sender's plan, whether a P2P recipient has finished a download, and
 *                 whether this sender has already taken the offer
 */
export function shouldAskForFirstMonthOffer(audience: FirstMonthOfferAudience): boolean {
	return !silentFor(audience);
}

/** Everything the post-signup moment needs in order to decide whether it exists. */
export interface PostSignupUpsellInput {
	/** The reader's current plan. */
	plan: Plan;
	/** What `GET /pay/upsell-offer` quoted, or null while unasked, in flight, or refused. */
	quotedPercentOff: number | null;
	/** The reader has closed this offer on this browser (see `./offerDismissal`). */
	dismissed: boolean;
	/**
	 * The offers yield to the welcome price (`pricing/welcomePrice.offersYieldToWelcome`): a welcome run
	 * is on, sold out or not, OR the read of it has not answered yet (bounded by `WELCOME_READ_TIMEOUT_MS`).
	 * Owner 2026-10-27, kitecorp/transferify#536: this offer stands aside too.
	 */
	welcomeOn: boolean;
}

/**
 * The offer shown on the files panel after account creation, or null for none
 * (kitecorp/transferify-ui#1448) — the second moment of kitecorp/transferify#117.
 *
 * <b>There is no "was this a signup?" input, and its absence is the design.</b> The moment used to be
 * conceived as a fixed window after account creation, which would need a signup timestamp the account
 * payload does not carry. It does not need one: the api decides WHEN there is anything to offer — the
 * campaign window, the monthly redemption cap and the coupon's own {@code valid} flag all live behind
 * {@code GET /pay/upsell-offer} — and it decides WHO, through a never-had-a-paid-plan floor no client
 * can widen. What is left for the client is the reader's own answer, which is the dismissal.
 *
 * <b>The subscriber check is deliberately duplicated here</b>, exactly as in {@link postSendUpsell}:
 * "a user who already has a subscription is never shown it" is a client-visible promise, and a promise
 * proven only by a server rule stops being proven the moment that rule is asked a different question.
 */
export function postSignupUpsell(input: PostSignupUpsellInput): FirstMonthOffer | null {
	if (input.dismissed || input.welcomeOn || isPaid(input.plan)) {
		return null;
	}
	return firstMonthOfferFrom(input.quotedPercentOff);
}

/**
 * Whether it is worth ASKING the api about this reader at all.
 *
 * The gates {@link postSignupUpsell} applies, minus the api's answer — the twin of
 * {@link shouldAskForFirstMonthOffer} and for the same reason: this callout sits on the files panel,
 * which every signed-in reader loads, so an ungated ask would spend a live Stripe read on every page
 * load rather than on offers actually shown.
 *
 * <b>`sessionReady` is the third gate, and it is not an optimization.</b> `GET /pay/upsell-offer` is
 * AUTHENTICATED — the answer depends on the account's own history — so an ask made before the auth
 * listener has settled goes out with no bearer and comes back 401. That failure is swallowed (a
 * promotions lookup that fails must not become a visible error), the effect does not retry, and the
 * offer is then absent for the whole page load: a signed-in, eligible reader silently sees nothing.
 * This is not hypothetical — it is what the first local-stack run of this feature actually did, and
 * an unauthenticated reader is indistinguishable from an ineligible one at every layer below.
 *
 * {@link shouldAskForFirstMonthOffer} needs no such gate because its moment cannot arrive before the
 * session has: a transfer has to complete first.
 *
 * @param plan         the reader's current plan
 * @param dismissed    the reader has already closed this offer on this browser
 * @param sessionReady the app session has reached its authenticated state, so a request will carry a
 *                     bearer the api accepts
 */
export function shouldAskForPostSignupOffer(
	plan: Plan, dismissed: boolean, sessionReady: boolean,
): boolean {
	return sessionReady && !dismissed && !isPaid(plan);
}

/**
 * Whether a Free reader who has HIT the monthly download cap should be offered the discount
 * (kitecorp/transferify-ui#1450).
 *
 * <p><strong>"reached", never "warning".</strong> The ticket's moment is the one where the value of
 * upgrading is most concrete — the cap actually hit, not approached. At 80% the reader still has
 * downloads left and nothing has been taken from them; a discount there is an interruption, and it
 * spends one of the campaign's capped redemptions on somebody who was not yet blocked.
 *
 * <p>The paid check is duplicated here for the same reason it is in {@link postSignupUpsell}: "a user
 * who already has a subscription is never shown it" is a client-visible promise, and a promise proven
 * only by a server rule stops being proven the moment that rule is asked a different question.
 *
 * @param capReached the reader has hit their monthly download cap
 * @param plan       the reader's current plan
 */
export function shouldAskForLimitOffer(capReached: boolean, plan: Plan): boolean {
	return capReached && !isPaid(plan);
}

/**
 * The offer to show at a hit limit, or null for none (kitecorp/transferify-ui#1450).
 *
 * Same shape as the other moments: the trigger, the plan, and the api's answer, with the api owning
 * whether there is a campaign to speak for at all.
 */
export function limitUpsell(input: {capReached: boolean; plan: Plan; quotedPercentOff: number | null}):
		FirstMonthOffer | null {
	if (!shouldAskForLimitOffer(input.capReached, input.plan)) {
		return null;
	}
	return firstMonthOfferFrom(input.quotedPercentOff);
}
